/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const APPROVE_DELIVERY_DISCRIMINATOR = new Uint8Array([
  28, 233, 51, 115, 33, 220, 41, 28,
]);

export function getApproveDeliveryDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    APPROVE_DELIVERY_DISCRIMINATOR,
  );
}

export type ApproveDeliveryInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountEscrow extends string | AccountMeta<string> = string,
  TAccountEscrowVault extends string | AccountMeta<string> = string,
  TAccountAgentTokenAccount extends string | AccountMeta<string> = string,
  TAccountClient extends string | AccountMeta<string> = string,
  TAccountTokenProgram extends string | AccountMeta<string> =
    "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountEscrow extends string
        ? WritableAccount<TAccountEscrow>
        : TAccountEscrow,
      TAccountEscrowVault extends string
        ? WritableAccount<TAccountEscrowVault>
        : TAccountEscrowVault,
      TAccountAgentTokenAccount extends string
        ? WritableAccount<TAccountAgentTokenAccount>
        : TAccountAgentTokenAccount,
      TAccountClient extends string
        ? ReadonlySignerAccount<TAccountClient> &
            AccountSignerMeta<TAccountClient>
        : TAccountClient,
      TAccountTokenProgram extends string
        ? ReadonlyAccount<TAccountTokenProgram>
        : TAccountTokenProgram,
      ...TRemainingAccounts,
    ]
  >;

export type ApproveDeliveryInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type ApproveDeliveryInstructionDataArgs = {};

export function getApproveDeliveryInstructionDataEncoder(): FixedSizeEncoder<ApproveDeliveryInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({ ...value, discriminator: APPROVE_DELIVERY_DISCRIMINATOR }),
  );
}

export function getApproveDeliveryInstructionDataDecoder(): FixedSizeDecoder<ApproveDeliveryInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getApproveDeliveryInstructionDataCodec(): FixedSizeCodec<
  ApproveDeliveryInstructionDataArgs,
  ApproveDeliveryInstructionData
> {
  return combineCodec(
    getApproveDeliveryInstructionDataEncoder(),
    getApproveDeliveryInstructionDataDecoder(),
  );
}

export type ApproveDeliveryInput<
  TAccountEscrow extends string = string,
  TAccountEscrowVault extends string = string,
  TAccountAgentTokenAccount extends string = string,
  TAccountClient extends string = string,
  TAccountTokenProgram extends string = string,
> = {
  escrow: Address<TAccountEscrow>;
  escrowVault: Address<TAccountEscrowVault>;
  agentTokenAccount: Address<TAccountAgentTokenAccount>;
  client: TransactionSigner<TAccountClient>;
  tokenProgram?: Address<TAccountTokenProgram>;
};

export function getApproveDeliveryInstruction<
  TAccountEscrow extends string,
  TAccountEscrowVault extends string,
  TAccountAgentTokenAccount extends string,
  TAccountClient extends string,
  TAccountTokenProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: ApproveDeliveryInput<
    TAccountEscrow,
    TAccountEscrowVault,
    TAccountAgentTokenAccount,
    TAccountClient,
    TAccountTokenProgram
  >,
  config?: { programAddress?: TProgramAddress },
): ApproveDeliveryInstruction<
  TProgramAddress,
  TAccountEscrow,
  TAccountEscrowVault,
  TAccountAgentTokenAccount,
  TAccountClient,
  TAccountTokenProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    escrow: { value: input.escrow ?? null, isWritable: true },
    escrowVault: { value: input.escrowVault ?? null, isWritable: true },
    agentTokenAccount: {
      value: input.agentTokenAccount ?? null,
      isWritable: true,
    },
    client: { value: input.client ?? null, isWritable: false },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.escrow),
      getAccountMeta(accounts.escrowVault),
      getAccountMeta(accounts.agentTokenAccount),
      getAccountMeta(accounts.client),
      getAccountMeta(accounts.tokenProgram),
    ],
    data: getApproveDeliveryInstructionDataEncoder().encode({}),
    programAddress,
  } as ApproveDeliveryInstruction<
    TProgramAddress,
    TAccountEscrow,
    TAccountEscrowVault,
    TAccountAgentTokenAccount,
    TAccountClient,
    TAccountTokenProgram
  >);
}

export type ParsedApproveDeliveryInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    escrow: TAccountMetas[0];
    escrowVault: TAccountMetas[1];
    agentTokenAccount: TAccountMetas[2];
    client: TAccountMetas[3];
    tokenProgram: TAccountMetas[4];
  };
  data: ApproveDeliveryInstructionData;
};

export function parseApproveDeliveryInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedApproveDeliveryInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 5) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      escrow: getNextAccount(),
      escrowVault: getNextAccount(),
      agentTokenAccount: getNextAccount(),
      client: getNextAccount(),
      tokenProgram: getNextAccount(),
    },
    data: getApproveDeliveryInstructionDataDecoder().decode(instruction.data),
  };
}
